import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "lucide-react";
import CustomDrawer from "@/components/common/drawer";
import PrimaryButton from "@/b2b/components/PrimaryButton.jsx";
import {
  fetchGromorEsignStatus,
  gromorAgreementUrl,
  pollGromorEsignUntil,
  recordGromorEsignConsent,
} from "@/lib/gromorEsign";

// pdf.js is ~350 KB gzipped; load it only once there is an agreement to show.
const PdfPages = lazy(() => import("./PdfPages"));

const WAIT_STAGES = [
  { atMs: 0, text: "Preparing your agreement…" },
  { atMs: 20000, text: "Still preparing — the lender is generating your document." },
  { atMs: 60000, text: "This is taking longer than usual. We're still on it." },
];
const WAIT_GIVE_UP_MS = 180000;

export default function GromorEsignFlow({ journey, isOpen, onClose, lender }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [waitText, setWaitText] = useState(WAIT_STAGES[0].text);
  const [gaveUp, setGaveUp] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState("");
  const cancelled = useRef(false);

  const partner = localStorage.getItem("partnerIdentifier");
  const userIdentifier = localStorage.getItem("user_identifier");
  const hubUrl = `/b/${userIdentifier}?partner=${partner}&step=onboarding`;
  const mandateUrl = `/b/${userIdentifier}?partner=${partner}&step=mandate`;

  // Wait for the agreement. Runs when the drawer opens; stops when it closes or unmounts.
  useEffect(() => {
    if (!isOpen) return undefined;
    cancelled.current = false;
    setGaveUp(false);

    const timers = WAIT_STAGES.map((s) => setTimeout(() => setWaitText(s.text), s.atMs));
    const giveUp = setTimeout(() => setGaveUp(true), WAIT_GIVE_UP_MS);

    (async () => {
      const result = await pollGromorEsignUntil(
        (s) => s.agreementReady || s.complete,
        { intervalMs: 3000, maxMs: WAIT_GIVE_UP_MS }
      );
      if (cancelled.current) return;
      if (result) setStatus(result);
      if (result?.complete) navigate(mandateUrl);
    })();

    return () => {
      cancelled.current = true;
      timers.forEach(clearTimeout);
      clearTimeout(giveUp);
    };
  }, [isOpen]);

  // Poll again the moment the tab comes back — the tenant may have left and returned.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onVisible = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const s = await fetchGromorEsignStatus();
        if (!cancelled.current && s) setStatus(s);
      } catch {
        /* a failed refresh is not a failed eSign */
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isOpen]);

  const sign = async () => {
    setSigning(true);
    setError("");
    try {
      // The backend refuses to record a signature it cannot attribute — missing IP or user
      // agent is a 400 with a message we can show, never a consent from "Unknown".
      const result = await recordGromorEsignConsent();
      if (result?.complete) {
        navigate(mandateUrl);
        return;
      }
      const settled = await pollGromorEsignUntil((s) => s.complete, {
        intervalMs: 3000,
        maxMs: 120000,
      });
      if (settled?.complete) {
        navigate(mandateUrl);
        return;
      }
      setError("Your consent was recorded but we could not confirm the signature yet. Please try again in a minute or contact support.");
    } catch (e) {
      setError(e?.message || "We could not record your consent. Please try again.");
    } finally {
      setSigning(false);
    }
  };

  const ready = Boolean(status?.agreementReady);

  return (
    <CustomDrawer isOpen={isOpen} closeDrawer={onClose}>
      <div className="h-full w-full flex flex-col relative">
        {!ready ? (
          <div className="flex-1 flex flex-col items-center justify-center text-white px-8 text-center gap-4">
            <div className="animate-pulse text-base">{waitText}</div>
            <div className="text-xs text-gray-400">
              {lender?.legalName || "The lender"} is generating your loan agreement. This usually
              takes under two minutes.
            </div>
            {gaveUp && (
              <div className="mt-6 flex flex-col gap-3 w-full">
                <div className="text-xs text-gray-300">
                  We'll keep preparing it in the background. You can come back any time from the
                  E-Sign card on your journey.
                </div>
                <PrimaryButton text="Back to my journey" handleClick={() => navigate(hubUrl)} />
              </div>
            )}
          </div>
        ) : (
          <>
            {/* The document IS the page, drawn by us so it looks the same on every phone.
                Gromor's agreement carries their Key Fact Statement. */}
            <Suspense
              fallback={
                <div className="flex-1 flex items-center justify-center text-white text-sm animate-pulse">
                  Loading your agreement…
                </div>
              }
            >
              <PdfPages
                url={gromorAgreementUrl(journey?.user?.userId, journey?.refId)}
                className="flex-1 min-h-0"
              />
            </Suspense>
            <div className="bg-[#1a1a1a] p-4 text-white w-full border-t border-[#444]">
              <p className="text-3xs mb-2">
                I,{" "}
                <strong>
                  {journey?.user?.firstName} {journey?.user?.lastName}
                </strong>
                , confirm that I have read and understood the Loan Agreement above, including
                its Key Fact Statement, from {lender?.legalName || "the lender"}, and I accept
                it. I understand that ticking this box is my signature.
              </p>
              <div className="flex items-center space-x-3 mb-3">
                <input
                  type="checkbox"
                  id="gromorConsent"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="cursor-pointer"
                />
                <label htmlFor="gromorConsent" className="cursor-pointer">
                  I Agree
                </label>
              </div>
              <PrimaryButton
                text="Complete eSign"
                loading={signing}
                loadingText="Recording your signature…"
                handleClick={sign}
                disabled={!agreed || signing}
                error={error}
              />
            </div>
          </>
        )}

        <button
          onClick={onClose}
          className="absolute top-3 left-3 p-1.5 cursor-pointer bg-white/90 shadow rounded-full flex items-center justify-center"
          aria-label="Back to summary"
        >
          <ChevronLeftIcon className="text-black w-5 h-5" />
        </button>
      </div>
    </CustomDrawer>
  );
}
